import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, ArrowLeftRight, Plus, X } from 'lucide-react';
import { contiMediciAPI } from '../../services/contiMediciApi';
import { magazzinoAPI } from '../../services/magazzinoApi';
import { mediciAPI } from '../../services/api';
import RiquadroRiconciliazione, { num } from './RiquadroRiconciliazione';

/**
 * Conti prodotto per medico.
 *
 * Lo stesso componente serve due porte diverse, ed e' voluto: la
 * linguetta del Magazzino, dove la segreteria assegna e travasa, e la
 * schermata del medico, che vede soltanto il proprio conto. Cambia chi
 * risponde alla stessa rotta, non il disegno: il server manda a un medico
 * solo le sue righe e non manda `coordina`, e i comandi di scrittura
 * compaiono solo quando `coordina` arriva vero.
 *
 * Quello che NON c'e', e non e' una dimenticanza: nessun importo. Il
 * conto dice quante unita', non quanto valgono. Se il residuo di un
 * medico vada addebitato e' una decisione del commercialista, e questa
 * schermata non la anticipa.
 */

const dataIta = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return isNaN(d) ? '—' : d.toLocaleDateString('it-IT');
};

const messaggio = (e, ripiego) =>
    e?.response?.data?.message || e?.message || ripiego;

const ContiMedici = ({ lottoIniziale = null, soloMio = false }) => {
    const [conti, setConti] = useState([]);
    const [riconciliazione, setRiconciliazione] = useState(null);
    const [coordina, setCoordina] = useState(false);
    const [caricamento, setCaricamento] = useState(true);
    const [errore, setErrore] = useState(null);
    const [avviso, setAvviso] = useState(null);
    const [inCorso, setInCorso] = useState(false);

    const [filtroLotto, setFiltroLotto] = useState(lottoIniziale);
    const [soloRosso, setSoloRosso] = useState(false);
    const [aperti, setAperti] = useState({});

    // Per i moduli: l'elenco dei lotti e quello dei medici
    const [lotti, setLotti] = useState([]);
    const [medici, setMedici] = useState([]);
    const [modulo, setModulo] = useState(null);   // 'assegna' | 'travaso' | null
    const [formA, setFormA] = useState({ lotto_id: '', righe: [{ medico_id: '', quantita: '' }], note: '' });
    const [formT, setFormT] = useState({ lotto_id: '', da_medico_id: '', a_medico_id: '', quantita: '', note: '' });
    const [daAssegnare, setDaAssegnare] = useState(null);

    /* LA CORREZIONE SI APRE SU UNA RIGA DI LOTTO, non sul totale del
       prodotto: l'assegnazione vive per lotto, e correggere «il totale»
       vorrebbe dire scegliere al posto di chi corregge da quale lotto
       togliere. */
    const [correggi, setCorreggi] = useState(null);      // { c, l }
    const [formC, setFormC] = useState({ giusto: '', motivo: '' });
    /* La storia si chiede solo quando qualcuno la apre: e' un'altra
       chiamata, e finche' nessuno guarda non serve a niente. */
    const [storia, setStoria] = useState({});
    const [storiaAperta, setStoriaAperta] = useState({});

    const carica = useCallback(async () => {
        try {
            setCaricamento(true);
            setErrore(null);
            const params = {};
            if (filtroLotto) params.lotto_id = filtroLotto;
            const r = await contiMediciAPI.elenco(params);
            setConti(r.data.data.conti || []);
            setRiconciliazione(r.data.data.riconciliazione || null);
            setCoordina(Boolean(r.data.data.coordina));
        } catch (e) {
            setErrore(messaggio(e, 'Errore nel recupero dei conti'));
        } finally {
            setCaricamento(false);
        }
    }, [filtroLotto]);

    useEffect(() => { carica(); }, [carica]);

    /* Lotti e medici servono solo a chi assegna: a un medico la rotta
       dei lotti risponde 403, ed e' giusto cosi'. Si chiedono insieme
       perche' i due moduli li vogliono tutti e due, e separarli
       farebbe comparire una tendina prima dell'altra. */
    useEffect(() => {
        if (soloMio || !coordina) return;
        let vivo = true;
        (async () => {
            try {
                const [l, m] = await Promise.all([
                    magazzinoAPI.lotti({}),
                    mediciAPI.getAll(true),
                ]);
                if (!vivo) return;
                setLotti(l.data.data.lotti || []);
                setMedici(m.data.data.medici || []);
            } catch {
                /* Senza le due tendine il conto resta consultabile: e'
                   la lettura che conta, e un errore qui parlerebbe di
                   una comodita' che manca, non di un dato sbagliato. */
            }
        })();
        return () => { vivo = false; };
    }, [coordina, soloMio]);

    // Quanto di un lotto non e' ancora di nessuno: si chiede quando si
    // sceglie il lotto, perche' e' li' che serve saperlo.
    useEffect(() => {
        const id = modulo === 'assegna' ? formA.lotto_id : formT.lotto_id;
        if (!id) { setDaAssegnare(null); return; }
        let vivo = true;
        (async () => {
            try {
                const r = await contiMediciAPI.perLotto(id);
                if (vivo) setDaAssegnare(r.data.data.da_assegnare);
            } catch { if (vivo) setDaAssegnare(null); }
        })();
        return () => { vivo = false; };
    }, [modulo, formA.lotto_id, formT.lotto_id]);

    const righeMostrate = soloRosso
        ? conti.filter((c) => parseFloat(c.residuo) < 0)
        : conti;
    const inRosso = conti.filter((c) => parseFloat(c.residuo) < 0);

    const nomeMedico = (c) =>
        `${c.medico_titolo || ''} ${c.medico_cognome} ${c.medico_nome}`.trim();

    /* A un medico la colonna del medico ripete il suo nome su ogni riga:
       e' l'unica risposta che il server gli manda. Si toglie, e si decide
       da `coordina` e non dalla proprieta' `soloMio`, perche' a stabilire
       che cosa si vede e' la risposta del server. */
    const mostraMedico = coordina;

    const chiave = (c) => `${c.medico_id}-${c.prodotto_id}`;

    const chiaveLotto = (c, l) => `${c.medico_id}-${l.lotto_id}`;

    const apriStoria = async (c, l) => {
        const k = chiaveLotto(c, l);
        setStoriaAperta((s) => ({ ...s, [k]: !s[k] }));
        if (storia[k]) return;
        try {
            const r = await contiMediciAPI.movimenti({
                medico_id: c.medico_id, lotto_id: l.lotto_id,
            });
            setStoria((s) => ({ ...s, [k]: r.data.data.movimenti || [] }));
        } catch (e) {
            setErrore(messaggio(e, 'Non riesco a leggere la storia di questa riga'));
        }
    };

    /* LA DIFFERENZA LA CALCOLA IL PROGRAMMA, e non e' un vezzo: chiedere
       «di quanto vuoi rettificare» vuol dire chiedere una sottrazione a
       mente su un numero con quattro decimali, e un segno sbagliato
       raddoppia l'errore invece di toglierlo. Qui si scrive quanto
       DOVEVA essere, e la riga col segno la compone il codice.
       L'arrotondamento a quattro decimali e' quello della colonna
       (NUMERIC(12,4)): senza, 20 meno 6,6 darebbe 13,399999999999999. */
    const differenza = correggi && String(formC.giusto).trim() !== ''
        ? Math.round((parseFloat(formC.giusto) - parseFloat(correggi.l.assegnato)) * 10000) / 10000
        : NaN;

    const salvaCorrezione = async () => {
        try {
            setInCorso(true);
            setErrore(null);
            const r = await contiMediciAPI.rettifica({
                lotto_id: correggi.l.lotto_id,
                medico_id: correggi.c.medico_id,
                quantita: differenza,
                note: formC.motivo.trim(),
            });
            setAvviso(r.data.message);
            /* La storia di quella riga e' cambiata: si butta la copia che
               avevamo, cosi' riaprendola si rilegge invece di mostrare
               quella di un minuto fa senza la riga appena scritta. */
            setStoria((s) => {
                const c = { ...s };
                delete c[chiaveLotto(correggi.c, correggi.l)];
                return c;
            });
            setCorreggi(null);
            setFormC({ giusto: '', motivo: '' });
            await carica();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nella registrazione della correzione'));
        } finally {
            setInCorso(false);
        }
    };

    const salvaAssegnazione = async () => {
        try {
            setInCorso(true);
            setErrore(null);
            const righe = formA.righe
                .filter((r) => r.medico_id && parseFloat(r.quantita) > 0)
                .map((r) => ({ medico_id: Number(r.medico_id), quantita: parseFloat(r.quantita) }));
            if (!formA.lotto_id || !righe.length) {
                setErrore('Serve il lotto e almeno una riga con medico e quantità.');
                return;
            }
            const r = await contiMediciAPI.assegna(Number(formA.lotto_id), righe, formA.note || null);
            setAvviso(r.data.message);
            setModulo(null);
            setFormA({ lotto_id: '', righe: [{ medico_id: '', quantita: '' }], note: '' });
            await carica();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nella registrazione'));
        } finally {
            setInCorso(false);
        }
    };

    const salvaTravaso = async () => {
        try {
            setInCorso(true);
            setErrore(null);
            const r = await contiMediciAPI.travaso({
                lotto_id: Number(formT.lotto_id),
                da_medico_id: Number(formT.da_medico_id),
                a_medico_id: Number(formT.a_medico_id),
                quantita: parseFloat(formT.quantita),
                note: formT.note || null,
            });
            setAvviso(r.data.message);
            setModulo(null);
            setFormT({ lotto_id: '', da_medico_id: '', a_medico_id: '', quantita: '', note: '' });
            await carica();
        } catch (e) {
            setErrore(messaggio(e, 'Errore nella registrazione del travaso'));
        } finally {
            setInCorso(false);
        }
    };

    return (
        <div className="mag-riquadro">
            {errore && <div className="mag-errore">{errore}</div>}
            {avviso && <div className="mag-avviso">{avviso}</div>}

            {/* ---------- in testa: chi e' in rosso ---------- */}
            {inRosso.length > 0 && (
                <div className="mag-conti-testa mag-conti-testa--allarme">
                    <p className="mag-critico">
                        <AlertTriangle size={16} />{' '}
                        {inRosso.length === 1
                            ? 'Un conto è in rosso:'
                            : `${inRosso.length} conti sono in rosso:`}{' '}
                        {inRosso.map((c) => `${c.medico_cognome} · ${c.prodotto} (${num(c.residuo)} ${c.unita_misura || ''})`).join(' · ')}
                    </p>
                    <p className="mag-sub">
                        Un conto in rosso non blocca niente: la procedura si registra
                        comunque. Si riassegna o si addebita a fine mese.
                    </p>
                </div>
            )}

            {/* ---------- il non attribuito, solo a chi coordina ---------- */}
            <RiquadroRiconciliazione riconciliazione={riconciliazione} />

            {/* ---------- comandi ---------- */}
            <div className="mag-filtri" data-non-trattiene>
                <label className="mag-spunta">
                    <input type="checkbox" checked={soloRosso}
                           onChange={(e) => setSoloRosso(e.target.checked)} />
                    solo i conti in rosso
                </label>
                {filtroLotto && (
                    <button className="mag-btn" onClick={() => setFiltroLotto(null)}>
                        <X size={14} /> togli il filtro sul lotto
                    </button>
                )}
                {coordina && !soloMio && (
                    <>
                        <button className="mag-btn mag-btn--principale"
                                onClick={() => { setModulo('assegna'); setErrore(null); }}>
                            {/* «anche a un medico nuovo»: il conto di un medico nasce
                                con la sua prima assegnazione, e la tendina li ha tutti.
                                Il pulsante diceva solo «Assegna un carico», e chi cercava
                                come aggiungere un medico al conto non lo trovava (22
                                settembre 2026). */}
                            <Plus size={14} /> Assegna un carico (anche a un medico nuovo)
                        </button>
                        <button className="mag-btn"
                                onClick={() => { setModulo('travaso'); setErrore(null); }}>
                            <ArrowLeftRight size={14} /> Registra un travaso
                        </button>
                    </>
                )}
            </div>

            {/* ---------- la tabella ---------- */}
            {caricamento ? (
                <p className="mag-vuoto">Caricamento…</p>
            ) : righeMostrate.length === 0 ? (
                <p className="mag-vuoto">
                    {soloRosso
                        ? 'Nessun conto in rosso.'
                        : 'Nessun conto: nessun carico è ancora stato assegnato a un medico.'}
                </p>
            ) : (
                <table className="mag-tabella rm-scorre">
                    <thead>
                        <tr>
                            {mostraMedico && <th>Medico</th>}
                            <th>Prodotto</th>
                            <th className="num">Assegnato</th>
                            <th className="num">Consumato</th>
                            <th className="num">Residuo</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {righeMostrate.map((c) => {
                            const rosso = parseFloat(c.residuo) < 0;
                            const apert = aperti[chiave(c)];
                            return (
                                <React.Fragment key={chiave(c)}>
                                    <tr className={rosso ? 'mag-riga-allarme' : ''}>
                                        {mostraMedico && <td>{nomeMedico(c)}</td>}
                                        <td>
                                            {c.prodotto}
                                            <span className="mag-sub">{c.prodotto_codice}</span>
                                        </td>
                                        <td className="num">{num(c.assegnato)}</td>
                                        <td className="num">{num(c.consumato)}</td>
                                        <td className={`num ${rosso ? 'mag-critico' : ''}`}>
                                            {num(c.residuo)} {c.unita_misura}
                                        </td>
                                        <td className="num">
                                            <button className="mag-btn"
                                                    onClick={() => setAperti((a) => ({
                                                        ...a, [chiave(c)]: !a[chiave(c)],
                                                    }))}>
                                                {apert ? 'chiudi' : 'per lotto'}
                                            </button>
                                        </td>
                                    </tr>
                                    {apert && (c.lotti || []).map((l) => [
                                        <tr key={`${chiave(c)}-${l.lotto_id}`} className="mag-sub">
                                            {mostraMedico && <td />}
                                            <td>
                                                lotto {l.codice_lotto || '—'}
                                                <span className="mag-sub">
                                                    scade {dataIta(l.scadenza)} · {l.stato_lotto}
                                                </span>
                                            </td>
                                            <td className="num">{num(l.assegnato)}</td>
                                            <td className="num">{num(l.consumato)}</td>
                                            <td className={`num ${parseFloat(l.residuo) < 0 ? 'mag-critico' : ''}`}>
                                                {num(l.residuo)}
                                            </td>
                                            <td className="num">
                                                {coordina && (
                                                    <button className="mag-btn"
                                                            onClick={() => {
                                                                setCorreggi({ c, l });
                                                                setFormC({
                                                                    giusto: String(l.assegnato),
                                                                    motivo: '',
                                                                });
                                                                setErrore(null);
                                                            }}>
                                                        Correggi
                                                    </button>
                                                )}
                                                <button className="mag-btn"
                                                        onClick={() => apriStoria(c, l)}>
                                                    {storiaAperta[chiaveLotto(c, l)] ? 'chiudi' : 'storia'}
                                                </button>
                                            </td>
                                        </tr>,
                                        /* LA STORIA STA SOTTO IL NUMERO, non in un'altra
                                           schermata: chi guarda un residuo che non torna
                                           deve poter leggere chi ha assegnato quanto e
                                           perche' senza cambiare pagina. */
                                        storiaAperta[chiaveLotto(c, l)] && (
                                            <tr key={`${chiave(c)}-${l.lotto_id}-storia`} className="mag-sub">
                                                {mostraMedico && <td />}
                                                <td colSpan={5}>
                                                    {!storia[chiaveLotto(c, l)] ? (
                                                        <span className="mag-sub">Caricamento…</span>
                                                    ) : storia[chiaveLotto(c, l)].length === 0 ? (
                                                        <span className="mag-sub">
                                                            Nessun movimento su questo lotto.
                                                        </span>
                                                    ) : (
                                                        <table className="mag-tabella rm-scorre">
                                                            <tbody>
                                                                {storia[chiaveLotto(c, l)].map((mv) => (
                                                                    <tr key={mv.id}>
                                                                        <td>{dataIta(mv.created_at)}</td>
                                                                        <td>{mv.tipo}</td>
                                                                        <td className="num">
                                                                            {parseFloat(mv.quantita) > 0 ? '+' : ''}
                                                                            {num(mv.quantita)}
                                                                        </td>
                                                                        <td>
                                                                            {mv.registrato_cognome
                                                                                ? `${mv.registrato_cognome} ${mv.registrato_nome || ''}`.trim()
                                                                                : '—'}
                                                                        </td>
                                                                        <td>
                                                                            {mv.note || '—'}
                                                                            {mv.controparte
                                                                                ? ` (con ${mv.controparte})` : ''}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    )}
                                                </td>
                                            </tr>
                                        ),
                                    ].filter(Boolean))}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            )}

            {/* ---------- assegnazione ---------- */}
            {modulo === 'assegna' && (
                <div className="mag-modale-sfondo" onClick={() => setModulo(null)}>
                    <div className="mag-modale" onClick={(e) => e.stopPropagation()}>
                        <div className="mag-modale-testata">
                            <b>Assegna un carico ai medici</b>
                            <button className="mag-icona" onClick={() => setModulo(null)}>
                                <X size={16} />
                            </button>
                        </div>
                        <div className="mag-modale-corpo">
                            <p className="mag-sub">
                                Le unità restano dove sono: l&apos;assegnazione dice di chi sono,
                                non le sposta in un altro magazzino. Nessun medico viene impedito
                                di usarne una.
                            </p>
                            <label className="mag-campo">
                                Lotto
                                <select value={formA.lotto_id}
                                        onChange={(e) => setFormA({ ...formA, lotto_id: e.target.value })}>
                                    <option value="">scegli…</option>
                                    {lotti.map((l) => (
                                        <option key={l.id} value={l.id}>
                                            {l.nome} · lotto {l.codice_lotto || 'senza numero'}
                                            {l.scadenza ? ` · scade ${dataIta(l.scadenza)}` : ''}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            {daAssegnare != null && (
                                <p className="mag-sub">Da assegnare su questo lotto: <b>{num(daAssegnare)}</b></p>
                            )}
                            <p className="mag-sub">
                                Puoi scegliere qualunque medico: se non ha ancora un conto, si apre adesso.
                            </p>
                            {formA.righe.map((r, i) => (
                                <div className="mag-riga-campi" key={i}>
                                    <label className="mag-campo">
                                        Medico
                                        <select value={r.medico_id}
                                                onChange={(e) => {
                                                    const righe = [...formA.righe];
                                                    righe[i] = { ...righe[i], medico_id: e.target.value };
                                                    setFormA({ ...formA, righe });
                                                }}>
                                            <option value="">scegli…</option>
                                            {medici.map((m) => (
                                                <option key={m.id} value={m.id}>
                                                    {m.cognome} {m.nome}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className="mag-campo">
                                        Quantità
                                        <input className="mag-qta" type="number" step="0.0001" min="0"
                                               value={r.quantita}
                                               onChange={(e) => {
                                                   const righe = [...formA.righe];
                                                   righe[i] = { ...righe[i], quantita: e.target.value };
                                                   setFormA({ ...formA, righe });
                                               }} />
                                    </label>
                                    {formA.righe.length > 1 && (
                                        <button className="mag-icona"
                                                onClick={() => setFormA({
                                                    ...formA,
                                                    righe: formA.righe.filter((_, j) => j !== i),
                                                })}>
                                            <X size={14} />
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button className="mag-btn"
                                    onClick={() => setFormA({
                                        ...formA,
                                        righe: [...formA.righe, { medico_id: '', quantita: '' }],
                                    })}>
                                <Plus size={14} /> un altro medico
                            </button>
                            <label className="mag-campo">
                                Nota
                                <input value={formA.note}
                                       onChange={(e) => setFormA({ ...formA, note: e.target.value })}
                                       placeholder="per esempio il documento di carico" />
                            </label>
                        </div>
                        <div className="mag-modale-azioni">
                            <button className="mag-btn" onClick={() => setModulo(null)}>Annulla</button>
                            <button className="mag-btn mag-btn--principale"
                                    disabled={inCorso} onClick={salvaAssegnazione}>
                                Assegna
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ---------- travaso ---------- */}
            {modulo === 'travaso' && (
                <div className="mag-modale-sfondo" onClick={() => setModulo(null)}>
                    <div className="mag-modale" onClick={(e) => e.stopPropagation()}>
                        <div className="mag-modale-testata">
                            <b>Registra un travaso fra medici</b>
                            <button className="mag-icona" onClick={() => setModulo(null)}>
                                <X size={16} />
                            </button>
                        </div>
                        <div className="mag-modale-corpo">
                            <p className="mag-sub">
                                La giacenza del lotto non cambia: cambia solo di chi sono le unità.
                            </p>
                            <label className="mag-campo">
                                Lotto
                                <select value={formT.lotto_id}
                                        onChange={(e) => setFormT({ ...formT, lotto_id: e.target.value })}>
                                    <option value="">scegli…</option>
                                    {lotti.map((l) => (
                                        <option key={l.id} value={l.id}>
                                            {l.nome} · lotto {l.codice_lotto || 'senza numero'}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <div className="mag-riga-campi">
                                <label className="mag-campo">
                                    Cede
                                    <select value={formT.da_medico_id}
                                            onChange={(e) => setFormT({ ...formT, da_medico_id: e.target.value })}>
                                        <option value="">scegli…</option>
                                        {medici.map((m) => (
                                            <option key={m.id} value={m.id}>{m.cognome} {m.nome}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="mag-campo">
                                    Riceve
                                    <select value={formT.a_medico_id}
                                            onChange={(e) => setFormT({ ...formT, a_medico_id: e.target.value })}>
                                        <option value="">scegli…</option>
                                        {medici.map((m) => (
                                            <option key={m.id} value={m.id}>{m.cognome} {m.nome}</option>
                                        ))}
                                    </select>
                                </label>
                                <label className="mag-campo">
                                    Quantità
                                    <input className="mag-qta" type="number" step="0.0001" min="0"
                                           value={formT.quantita}
                                           onChange={(e) => setFormT({ ...formT, quantita: e.target.value })} />
                                </label>
                            </div>
                            <label className="mag-campo">
                                Perché
                                <input value={formT.note}
                                       onChange={(e) => setFormT({ ...formT, note: e.target.value })}
                                       placeholder="per esempio: ha coperto una seduta" />
                            </label>
                        </div>
                        <div className="mag-modale-azioni">
                            <button className="mag-btn" onClick={() => setModulo(null)}>Annulla</button>
                            <button className="mag-btn mag-btn--principale"
                                    disabled={inCorso} onClick={salvaTravaso}>
                                Registra il travaso
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* ---------- correzione di un'assegnazione ---------- */}
            {correggi && (
                <div className="mag-modale-sfondo" onClick={() => setCorreggi(null)}>
                    <div className="mag-modale" onClick={(e) => e.stopPropagation()}>
                        <div className="mag-modale-testata">
                            <b>Correggi l&apos;assegnato</b>
                            <button className="mag-icona" onClick={() => setCorreggi(null)}>
                                <X size={16} />
                            </button>
                        </div>
                        <div className="mag-modale-corpo">
                            <p className="mag-sub">
                                {correggi.c.prodotto} · lotto {correggi.l.codice_lotto || 'senza numero'}
                                {mostraMedico ? ` · ${nomeMedico(correggi.c)}` : ''}
                            </p>
                            <p className="mag-sub">
                                Il registro non si riscrive: la correzione è una riga nuova con la
                                differenza e il motivo. Quello che c&apos;era resta, e si rilegge
                                con «storia» sotto la riga.
                            </p>
                            <p>
                                Adesso risulta assegnato: <b>{num(correggi.l.assegnato)}</b>
                                {correggi.c.unita_misura ? ` ${correggi.c.unita_misura}` : ''}
                            </p>
                            <label className="mag-campo">
                                Quanto doveva essere
                                <input className="mag-qta" type="number" step="0.0001" min="0"
                                       value={formC.giusto}
                                       onChange={(e) => setFormC({ ...formC, giusto: e.target.value })} />
                            </label>
                            {Number.isFinite(differenza) && (
                                <p className="mag-sub">
                                    {differenza === 0
                                        ? 'È lo stesso numero: non c\'è niente da correggere.'
                                        : `Verrà registrata una rettifica di ${differenza > 0 ? '+' : '-'}${num(Math.abs(differenza))}.`}
                                </p>
                            )}
                            <label className="mag-campo">
                                Perché *
                                <input value={formC.motivo}
                                       onChange={(e) => setFormC({ ...formC, motivo: e.target.value })}
                                       placeholder="per esempio: scritte 30 invece di 20 sul carico del 3 marzo" />
                            </label>
                            <p className="mag-sub">
                                Il motivo è obbligatorio: una correzione senza motivo non è
                                verificabile da chi la legge fra sei mesi.
                            </p>
                        </div>
                        <div className="mag-modale-azioni">
                            <button className="mag-btn" onClick={() => setCorreggi(null)}>Annulla</button>
                            <button className="mag-btn mag-btn--principale"
                                    disabled={inCorso || !Number.isFinite(differenza)
                                              || differenza === 0 || !formC.motivo.trim()}
                                    onClick={salvaCorrezione}>
                                Registra la correzione
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ContiMedici;
